import i18n from '@/i18n';
import { imageSpaceAPI } from '@/utils/api';
import { stripImageFileExtension } from '@/utils/imageUrlUtils';
import { showToast } from '@/utils/toast';
import { handleIpeError } from '@/pages/ipe/workbench/utils/errorHandler';
import {
  buildSmartToolResultRecordId,
  listStudioOutputImageSpaceIdCandidates,
} from '@/pages/ipe/workbench/utils/persistSmartToolResultsToImageSpace';

export const IMAGE_SPACE_RECORD_UPDATED_EVENT = 'ipe-image-space-record-updated';

function resolveImageRecord(payload) {
  const inner = payload?.image || payload?.data || payload;
  return inner && typeof inner === 'object' ? inner : null;
}

function normalizeUrlForMatch(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const parsed = new URL(raw);
      return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '');
    }
  } catch {
    /* ignore */
  }
  return raw.split('?')[0].split('#')[0].replace(/\/+$/, '');
}

function extractUrlLeaf(url) {
  const normalized = normalizeUrlForMatch(url);
  if (!normalized) return '';
  const parts = normalized.split('/').filter(Boolean);
  const leaf = parts[parts.length - 1] || '';
  return leaf.replace(/\.[a-z0-9]+$/i, '').toLowerCase();
}

function urlsLikelyMatch(a, b) {
  const left = normalizeUrlForMatch(a);
  const right = normalizeUrlForMatch(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.endsWith(right) || right.endsWith(left)) return true;
  const leftLeaf = extractUrlLeaf(left);
  const rightLeaf = extractUrlLeaf(right);
  return Boolean(leftLeaf && rightLeaf && leftLeaf === rightLeaf && leftLeaf.length >= 8);
}

function emitImageSpaceRecordUpdated(patch) {
  if (typeof window === 'undefined' || !patch?.id) return;
  window.dispatchEvent(new CustomEvent(IMAGE_SPACE_RECORD_UPDATED_EVENT, {
    detail: patch,
  }));
}

function toMetaRecord(record, fallbackId = '') {
  if (!record || typeof record !== 'object') return null;
  const resolvedId = String(record.id || fallbackId || '').trim();
  if (!resolvedId || resolvedId === 'image') return null;
  if (!record.url && !record.src && !record.imageUrl && !record.source && !record.sourceType && !record.createdAt) {
    return null;
  }
  return {
    id: resolvedId,
    name: String(record.name || '').trim(),
    favorited: record.favorite === true || record.isFavorite === true,
    url: String(record.url || record.src || record.imageUrl || '').trim(),
    record,
  };
}

/**
 * 详情磁贴本地 key（如 img-xxx-print-0 / img-xxx-cropped），不是图片空间 record id。
 * 对这些 id 发起 getById 必然 404，还会触发 client-error-log。
 */
export function isSyntheticImageSpaceProbeId(imageId) {
  const id = String(imageId || '').trim();
  if (!id || id === 'image') return true;
  return /-(?:print-\d+|cropped|original)$/i.test(id);
}

async function fetchImageSpaceRecordById(imageId) {
  const id = String(imageId || '').trim();
  if (!id || id === 'image' || isSyntheticImageSpaceProbeId(id)) return null;
  try {
    const res = await imageSpaceAPI.getById(id);
    return toMetaRecord(resolveImageRecord(res), id);
  } catch {
    return null;
  }
}

function collectCandidateIds({
  imageId = '',
  candidateIds = [],
  navId = '',
  taskId = '',
  outputIndex = null,
  output = null,
} = {}) {
  const ids = [];
  const push = (value) => {
    const id = String(value || '').trim();
    if (!id || id === 'image' || isSyntheticImageSpaceProbeId(id) || ids.includes(id)) return;
    ids.push(id);
  };
  push(imageId);
  (Array.isArray(candidateIds) ? candidateIds : []).forEach(push);
  if (navId && taskId) {
    listStudioOutputImageSpaceIdCandidates(navId, taskId, output || {}, outputIndex ?? 0)
      .forEach(push);
    push(buildSmartToolResultRecordId(navId, taskId, outputIndex ?? 0, output || {}));
  }
  return ids;
}

function recordMatchesUrl(img, targetUrl) {
  if (!targetUrl || !img) return false;
  const candidates = [img?.url, img?.src, img?.imageUrl, img?.displayUrl, img?.viewUrl];
  return candidates.some((candidate) => urlsLikelyMatch(candidate, targetUrl));
}

function recordMatchesTaskId(img, taskHint) {
  if (!taskHint || !img) return false;
  const aiTaskId = String(img?.aiTaskId || img?.taskId || '').trim();
  return Boolean(aiTaskId && (aiTaskId === taskHint || aiTaskId.startsWith(`${taskHint}-`)));
}

/** 按 URL / aiTaskId 在图片空间最近若干页中查找真实记录 */
export async function findImageSpaceRecordByHints({
  url = '',
  taskId = '',
  maxPages = 8,
  pageSize = 200,
} = {}) {
  const targetUrl = normalizeUrlForMatch(url);
  const taskHint = String(taskId || '').trim();
  if (!targetUrl && !taskHint) return null;

  for (let page = 1; page <= maxPages; page += 1) {
    let res;
    try {
      res = await imageSpaceAPI.getAll({ page, pageSize });
    } catch {
      break;
    }
    const images = Array.isArray(res?.images) ? res.images : [];
    // 有 URL 时只认 URL 命中，避免同 task 多图时误改第一张并假成功
    if (targetUrl) {
      const byUrl = images.find((img) => recordMatchesUrl(img, targetUrl));
      if (byUrl?.id) return toMetaRecord(byUrl);
    } else if (taskHint) {
      const byTask = images.find((img) => recordMatchesTaskId(img, taskHint));
      if (byTask?.id) return toMetaRecord(byTask);
    }
    if (res?.hasMore === false || images.length === 0) break;
  }
  return null;
}

/** @deprecated 使用 findImageSpaceRecordByHints */
export async function findImageSpaceRecordByUrl(url, options = {}) {
  return findImageSpaceRecordByHints({ url, ...options });
}

/**
 * 解析可写的图片空间记录：多候选 id → URL / taskId 回落。
 */
export async function resolveImageSpaceRecordForMutation({
  imageId = '',
  url = '',
  candidateIds = [],
  navId = '',
  taskId = '',
  outputIndex = null,
  output = null,
} = {}) {
  const ids = collectCandidateIds({
    imageId,
    candidateIds,
    navId,
    taskId,
    outputIndex,
    output,
  });
  for (const id of ids) {
    const byId = await fetchImageSpaceRecordById(id);
    if (byId) return byId;
  }
  return findImageSpaceRecordByHints({ url, taskId });
}

/**
 * 切换图片空间收藏；无有效 id 时提示并返回 null。
 * @returns {Promise<boolean|null>} 成功时返回新收藏状态
 */
export async function toggleImageCardFavorite(imageId, currentFavorited = false, options = {}) {
  const resolved = await resolveImageSpaceRecordForMutation({ imageId, ...options });
  if (!resolved?.id) {
    showToast(i18n.t('workbench:ipeWorkbench.cannotFavoriteNotIngested'), 'warning');
    return null;
  }
  const next = !currentFavorited;
  try {
    await imageSpaceAPI.update(resolved.id, { favorite: next, isFavorite: next });
    emitImageSpaceRecordUpdated({ id: resolved.id, favorite: next, isFavorite: next });
    showToast(next ? i18n.t('workbench:ipeWorkbench.addedToFavorites') : i18n.t('workbench:ipeWorkbench.removedFromFavorites'), 'success');
    return next;
  } catch (error) {
    showToast(handleIpeError(i18n.t('workbench:ipeUtilError.favoriteAction'), error, i18n.t('workbench:ipeWorkbench.favoriteFailed')), 'error');
    return null;
  }
}

/**
 * 重命名图片空间记录；确认名已含扩展名。
 * @returns {Promise<{ ok: boolean, id?: string, name?: string }>}
 */
export async function renameImageCard(imageId, nextFullName, options = {}) {
  const name = String(nextFullName || '').trim();
  if (!name) {
    showToast(i18n.t('workbench:ipeWorkbench.enterImageName'), 'warning');
    return { ok: false };
  }
  const resolved = await resolveImageSpaceRecordForMutation({ imageId, ...options });
  if (!resolved?.id) {
    showToast(i18n.t('workbench:ipeWorkbench.cannotRenameNotIngested'), 'warning');
    return { ok: false };
  }
  try {
    await imageSpaceAPI.update(resolved.id, { name });
    // 校验写回：后端曾对不存在 id 静默返回 ok
    const verified = await fetchImageSpaceRecordById(resolved.id);
    if (!verified || String(verified.name || '').trim() !== name) {
      showToast(i18n.t('workbench:ipeWorkbench.renameVerifyFailed'), 'error');
      return { ok: false };
    }
    emitImageSpaceRecordUpdated({ id: resolved.id, name });
    showToast(i18n.t('workbench:ipeWorkbench.nameUpdated'), 'success');
    return { ok: true, id: resolved.id, name };
  } catch (error) {
    showToast(handleIpeError(i18n.t('workbench:ipeUtilError.renameAction'), error, i18n.t('workbench:ipeWorkbench.renameFailed')), 'error');
    return { ok: false };
  }
}

/** 从图片空间拉取当前名称与收藏态（用于打开更多前补全） */
export async function fetchImageCardMeta(imageId, options = {}) {
  return resolveImageSpaceRecordForMutation({ imageId, ...options });
}

/** 弹窗确认时：草稿去后缀后拼回原扩展名 */
export function buildRenamedImageFileName(rawName, draftBase) {
  const raw = String(rawName || '');
  const trimmedBase = stripImageFileExtension(String(draftBase || '').trim())
    || String(draftBase || '').trim();
  if (!trimmedBase) return '';
  const extMatch = raw.match(/\.(png|jpe?g|webp|gif|bmp|svg|heic|avif|tiff?)$/i);
  return extMatch ? `${trimmedBase}${extMatch[0]}` : trimmedBase;
}
